<template>
  <div class="main-content">

    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-content">
        <div class="spinner-container">
          <div class="spinner"></div>
        </div>
        <h3>Importing Medications...</h3>
        <div class="progress-container">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: uploadProgress + '%' }"></div>
          </div>
          <p class="progress-text">{{ uploadProgress }}% uploaded</p>
        </div>
        <div class="time-info">
          <p><strong>Elapsed Time:</strong> {{ formatTime(elapsedTime) }}</p>
          <p v-if="estimatedTimeRemaining > 0"><strong>Estimated Time Remaining:</strong> {{ formatTime(estimatedTimeRemaining) }}</p>
        </div>
        <p class="loading-message">{{ loadingMessage }}</p>
      </div>
    </div>

    <div class="wrap-content container" id="container">
      <!-- PAGE TITLE -->
      <section id="page-title" style="background-color: #d1d1d1;">
        <div class="row">
          <div class="col-sm-10">
            <h1 class="mainTitle">All Medications</h1>
          </div>
          <div class="col-sm-2">
            <router-link
              v-if="hasPermission('medication create')"
              style="margin-right: 10px"
              class="btn btn-primary"
              :to="{ name: 'create-medication' }"
            >
              Create
            </router-link>
            <router-link
              v-if="hasPermission('medication getSoftDeleted')"
              class="btn btn-success"
              :to="{ name: 'deleted-medications' }"
            >
              Deleted Medication
            </router-link>
          </div>
          <div class="col-sm-2">
            <button class="btn btn-info" @click="exportMedications" style="margin-left: 5px;">
              Export Medications
            </button>

            <button
              v-if="fileReady"
              class="btn btn-success"
              @click="downloadMedications"
              style="margin-left: 5px;"
            >
              Download Medications
            </button>

            <hr />

            <input type="file" @change="handleFile" />
            <button class="btn btn-success" @click="importMedications">Import Medications</button>
          </div>
        </div>
      </section>

      <!-- DYNAMIC TABLE -->
      <div class="container-fluid container-fullw bg-white">
        <div class="row">
          <div class="col-md-12">
            <!-- ✅ استخدم الـ DataTable Component -->
          <DataTable
              :items="medications"
              :meta="meta"
              :loading="loading"
              :per-page="perPage"
              :columns="columns"
              :actions="true"
              search-placeholder="Search medications..."
              item-name="medications"
              @search="handleSearch"
              @per-page-change="handlePerPageChange"
              @page-change="handlePageChange"
            >
              <!-- ✅ Rows بس -->
              <template #default="{ items }">
                <tr v-for="medication in items" :key="medication.id">
                  <td>{{ medication.id }}</td>
                  <td>{{ medication.name_ar }}</td>
                  <td>{{ medication.name_en }}</td>
                  <td>{{ medication.type.name }}</td>
                  <td>
                    <img :src="medication.main_image" width="50" height="50" class="img-thumbnail" />
                  </td>
                  <td>
                    <button
                      class="me-2 btn btn-danger"
                      v-if="hasPermission('medication delete')"
                      @click="deleteMedcation(medication.id)"
                    >
                      Delete
                    </button>
                    <router-link
                      class="btn btn-primary spacing"
                      v-if="hasPermission('medication update')"
                      :to="{ name: 'edit-medication', params: { id: medication.id } }"
                    >
                      Edit
                    </router-link>
                  </td>
                </tr>
              </template>
            </DataTable>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { store } from '../../Store';
import DataTable from '../DataTable/DataTable.vue';  
import axios from '../../Axios';

export default {
  name: 'AllMedications',
  components: {
    DataTable, 
  },
  data() {
    return {
      medications: [],
      meta: {},
      search: '',
      perPage: 20,
      page: 1,
      loading: false,
      fileReady: false,
      file: null,
      isLoading: false,
      uploadProgress: 0,
      elapsedTime: 0,
      estimatedTimeRemaining: 0,
      loadingMessage: '',
      pollingInterval: null,
      timerInterval: null,
      columns: [
        { label: '#', key: 'id' },
        { label: 'Name Arabic', key: 'name_ar' },
        { label: 'Name English', key: 'name_en' },
        { label: 'Type', key: 'type.name' },
        { label: 'Image', key: 'main_image' },
      ],
    };
  },

  async created() {
    this.fetchMedications();
  },

  computed: {
    hasPermission() {
      return (permission) => store.hasPermission(permission);
    },
  },

  methods: {
    async fetchMedications(page = 1) {
      try {
        this.page = page;
        // this.loading = true;

        const res = await axios.get('/api/admin/medication/all', {
          params: {
            per_page: this.perPage,
            page: this.page,
            search: this.search,
          },
        });

        this.medications = res.data.data;
        this.meta = res.data.meta;
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    // ✅ Handlers للـ DataTable events
    handleSearch(searchTerm) {
      this.search = searchTerm;
      this.fetchMedications(1);
    },

    handlePerPageChange(perPage) {
      this.perPage = perPage;
      this.fetchMedications(1);
    },

    handlePageChange(page) {
      this.fetchMedications(page);
    },

    deleteMedcation(id) {
      if (!confirm('Are you sure you want to delete this medication?')) return;

      axios
        .delete(`/admin/admin/medication/delete?id=${id}`)
        .then(() => {
          this.fetchMedications(this.page);
        })
        .catch((error) => {
          console.error(error);
        });
    },

    async exportMedications() {
      try {
        await axios.get(`api/medications/export`);
        alert('Export job dispatched. File will be ready soon!');
        this.pollingInterval = setInterval(this.checkFileReady, 1000);
      } catch (error) {
        console.error('Error exporting medications:', error);
      }
    },

    async checkFileReady() {
      try {
        const res = await axios.get('api/admin/medications/check-file');
        if (res.data.ready) {
          this.fileReady = true;
          clearInterval(this.pollingInterval);
        }
      } catch (error) {
        console.error('Error checking file status:', error);
      }
    },

    async downloadMedications() {
      try {
        const response = await axios.get('/api/medications/download', {
          responseType: 'blob',
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'medications.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error('Error downloading file:', error);
      }
    },

 handleFile(e) {
      this.file = e.target.files[0];
    },
    async importMedications() {
      if (!this.file) return alert('Please select a file!');

      const formData = new FormData();
      formData.append('file', this.file);

      try {
        this.isLoading = true;
        this.uploadProgress = 0;
        this.elapsedTime = 0;
        this.estimatedTimeRemaining = 0;
        this.loadingMessage = 'Starting import...';

        // Start elapsed time counter
        this.timerInterval = setInterval(() => {
          this.elapsedTime++;
        }, 1000);

        // Simulate progress (or use API progress if available)
        const progressSimulator = setInterval(() => {
          if (this.uploadProgress < 90) this.uploadProgress += Math.floor(Math.random() * 5 + 1);
        }, 500);

        await axios.post('/api/admin/medications/import', formData);

        // Complete progress
        clearInterval(progressSimulator);
        this.uploadProgress = 100;
        this.loadingMessage = 'Import completed!';
        clearInterval(this.timerInterval);

        setTimeout(() => {
          this.isLoading = false;
          this.fetchMedications();  
        }, 1000);
      } catch (error) {
        clearInterval(this.timerInterval);
        this.isLoading = false;
        this.uploadProgress = 0;
        console.error('Import error:', error);
        alert('Failed to upload file!');
      }
    },
    formatTime(seconds) {
      const min = Math.floor(seconds / 60);
      const sec = seconds % 60;
      return `${min.toString().padStart(2, '0')}:${sec.toString().padStart(2, '0')}`;
    },
  },
};
</script>

<style scoped>
.table {
  margin-top: 10px;
}

.spacing {
  margin-left: 10px !important;
}

.loading-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex; justify-content: center; align-items: center;
  z-index: 9999;
}
.loading-content {
  background: #fff; padding: 20px; border-radius: 10px; width: 400px;
  text-align: center;
}
.spinner-container {
  margin-bottom: 15px;
}
.spinner {
  border: 4px solid #f3f3f3; border-top: 4px solid #3498db; border-radius: 50%;
  width: 40px; height: 40px; animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}
@keyframes spin {
  0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }
}
.progress-container { margin-top: 15px; }
.progress-bar {
  width: 100%; background: #eee; height: 20px; border-radius: 10px; overflow: hidden;
}
.progress-fill {
  height: 100%; background: #3498db; width: 0%;
  transition: width 0.3s ease;
}
.progress-text { margin-top: 5px; font-weight: bold; }
.time-info { margin-top: 10px; }
.loading-message { margin-top: 10px; font-style: italic; }
</style>